import { z } from 'zod'
import {
  TRAINER_CONTRACT_VERSION,
} from './trainer-event-read'
import {
  trainerEventFeedbackContextResponseSchema,
  trainerEventGroupWorkContextResponseSchema,
} from './trainer-event-feedback-read'
import {
  trainerInvoiceLineItemSchema,
  trainerInvoiceReadEvidenceSchema,
  trainerInvoiceReadSideEffectsSchema,
} from './trainer-invoice-read'
import {
  trainerCertificateParticipantSchema,
} from './trainer-certificate-read'

export const TRAINER_PUBLIC_READ_CONTRACT_VERSION = TRAINER_CONTRACT_VERSION

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

const nullableString = z.string().nullable()

/** Query keys accepted by Trainer public certificate/feedback/group-work GET routes. */
export const trainerPublicReadQuerySchema = z.object({
  orgId: optionalQueryString,
  token: optionalQueryString,
}).passthrough()

export const trainerPublicCertificateLegacyFallbackSchema = z.object({
  attempted: z.boolean(),
  hit: z.boolean(),
  copiedToOwnedDb: z.boolean(),
  sourceTable: nullableString,
  providerTouched: z.literal(false),
  rawEmailReturned: z.literal(false),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerPublicCertificateLookupSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  eventId: z.string().min(1),
  uid: z.string().min(1),
  formId: nullableString,
  sourceTable: z.string().min(1),
  workshopKey: nullableString,
  workshopName: nullableString,
  certificateDate: nullableString,
  participant: trainerCertificateParticipantSchema,
  trainerId: nullableString,
  location: nullableString,
  organization: nullableString,
  designation: nullableString,
  homeCity: nullableString,
  contentRating: nullableString,
  trainerRating: nullableString,
  heading: nullableString,
  text: z.string(),
  status: z.string().min(1),
  providerMode: z.string().min(1),
  emailStatus: z.string().min(1),
  memberpressStatus: z.string().min(1),
  thinkificStatus: z.string().min(1),
  kindeStatus: z.string().min(1),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

export const trainerPublicCertificateAccessSchema = z.object({
  status: z.enum(['active', 'revoked']),
  lookupScope: z.string().min(1),
  requiresPlatformLogin: z.literal(false),
  rawEmailReturned: z.literal(false),
  rawPayloadReturned: z.literal(false),
  providerTouched: z.literal(false),
  ambiguousUidRejected: z.literal(false),
  legacyFallback: trainerPublicCertificateLegacyFallbackSchema,
}).passthrough()

export const trainerPublicCertificateSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  db1Written: z.boolean(),
  rawEmailReturned: z.literal(false),
  rawPayloadReturned: z.literal(false),
  providerTouched: z.literal(false),
}).passthrough()

export const trainerPublicCertificateLookupResponseSchema = z.object({
  contract_version: z.literal(TRAINER_PUBLIC_READ_CONTRACT_VERSION),
  certificate: trainerPublicCertificateLookupSummarySchema.nullable(),
  publicAccess: trainerPublicCertificateAccessSchema,
  sideEffects: trainerPublicCertificateSideEffectsSchema,
}).passthrough()

export const trainerPublicCertificateEventSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  name: z.string(),
  eventDate: nullableString,
  courseType: nullableString,
  venueName: nullableString,
  city: nullableString,
  country: nullableString,
}).passthrough()

export const trainerPublicCertificateRecipientRenderingGateSchema = z.object({
  ownedTemplateParityReady: z.boolean(),
  status: z.string().min(1),
  blockingGate: nullableString,
  requiredEnvName: z.string().min(1),
  requiredValue: z.string().min(1),
  requiredEvidence: z.string().min(1),
}).passthrough()

export const trainerPublicCertificateAccessContractSchema = z.object({
  status: z.string().min(1),
  requiresPlatformLogin: z.literal(false),
  publicPath: nullableString,
  qrPayload: nullableString,
  providerMode: z.string().min(1),
  providerModes: z.record(z.string()),
  recipientRenderingGate: trainerPublicCertificateRecipientRenderingGateSchema,
}).passthrough()

export const trainerPublicCertificateWorkshopConfigSchema = z.object({
  key: z.string().min(1),
  workshopName: z.string().min(1),
  certificateHeading: z.string(),
  certificateText: z.string(),
  memberpressMembershipId: z.number().finite(),
  thinkificMembershipId: z.number().finite(),
}).passthrough()

export const trainerPublicCertificateContextSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  db1Written: z.literal(false),
  providerTouched: z.literal(false),
  rawPayloadReturned: z.literal(false),
  secretReturned: z.literal(false),
}).passthrough()

export const trainerPublicCertificateContextResponseSchema = z.object({
  contract_version: z.literal(TRAINER_PUBLIC_READ_CONTRACT_VERSION),
  event: trainerPublicCertificateEventSummarySchema,
  certificateAccess: trainerPublicCertificateAccessContractSchema,
  workshopConfig: trainerPublicCertificateWorkshopConfigSchema.nullable(),
  sideEffects: trainerPublicCertificateContextSideEffectsSchema,
}).passthrough()

export const trainerPublicInvoiceSharePaymentSchema = z.object({
  status: z.literal('available'),
  provider: z.literal('shared_billing_payment_link'),
  paymentLinkUrl: z.string().min(1),
  urlAllowlisted: z.literal(true),
  paymentProviderTouched: z.literal(false),
  rawProviderPayloadReturned: z.literal(false),
}).passthrough()

export const trainerPublicInvoiceShareInvoiceSchema = z.object({
  code: z.string().min(1),
  date: z.string(),
  dueDate: z.string(),
  amount: z.number().finite().nullable(),
  paid: z.boolean(),
  invoiceType: z.string(),
  clientName: nullableString,
  eventName: nullableString,
  invoiceItemName: nullableString,
  description: nullableString,
  quantity: z.number().finite().nullable(),
  pricePerQuantity: z.number().finite().nullable(),
  lineItems: z.array(trainerInvoiceLineItemSchema),
}).passthrough()

export const trainerPublicInvoiceShareAccessSchema = z.object({
  status: z.literal('active'),
  requiresPlatformLogin: z.literal(false),
  checkoutEnabled: z.literal(false),
  paymentProviderTouched: z.literal(false),
  providerTouched: z.literal(false),
  rawProviderPayloadReturned: z.literal(false),
  rawEmailReturned: z.literal(false),
}).passthrough()

export const trainerPublicInvoiceShareResponseSchema = z.object({
  contract_version: z.literal(TRAINER_PUBLIC_READ_CONTRACT_VERSION),
  invoice: trainerPublicInvoiceShareInvoiceSchema,
  publicAccess: trainerPublicInvoiceShareAccessSchema,
  payment: trainerPublicInvoiceSharePaymentSchema.nullable(),
  invoiceEvidence: trainerInvoiceReadEvidenceSchema,
  sideEffects: trainerInvoiceReadSideEffectsSchema,
}).passthrough()

export const trainerPublicFeedbackContextResponseSchema = trainerEventFeedbackContextResponseSchema
export const trainerPublicGroupWorkContextResponseSchema = trainerEventGroupWorkContextResponseSchema

export type TrainerPublicReadQuery = z.infer<typeof trainerPublicReadQuerySchema>
export type TrainerPublicCertificateLookupResponse = z.infer<typeof trainerPublicCertificateLookupResponseSchema>
export type TrainerPublicCertificateContextResponse = z.infer<typeof trainerPublicCertificateContextResponseSchema>
export type TrainerPublicInvoiceShareResponse = z.infer<typeof trainerPublicInvoiceShareResponseSchema>
export type TrainerPublicFeedbackContextResponse = z.infer<typeof trainerPublicFeedbackContextResponseSchema>
export type TrainerPublicGroupWorkContextResponse = z.infer<typeof trainerPublicGroupWorkContextResponseSchema>
